/**
 * Utility function to split a full name into first and last name components
 * Handles various name formats intelligently
 * 
 * This utility is used to pre-populate onboarding forms with names from the
 * displayName field captured during user signup, improving user experience
 * by reducing the need to re-enter names.
 */

export interface SplitNameResult {
  firstName: string;
  lastName: string;
}

/**
 * Splits a full name into first and last name components
 * @param fullName - The full name to split
 * @returns Object containing firstName and lastName
 */
export function splitFullName(fullName: string | null | undefined): SplitNameResult {
  // Handle null, undefined, or empty strings
  if (!fullName || typeof fullName !== 'string' || fullName.trim() === '') {
    return { firstName: '', lastName: '' };
  }

  // Trim whitespace and normalize spaces
  const normalizedName = fullName.trim().replace(/\s+/g, ' ');
  
  // Split by spaces
  const nameParts = normalizedName.split(' ');
  
  // Handle single name (treat as first name)
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: '' };
  }
  
  // Handle two names (most common case)
  if (nameParts.length === 2) {
    return { firstName: nameParts[0], lastName: nameParts[1] };
  }
  
  // Handle three or more names
  // Common patterns:
  // - "John Michael Smith" -> firstName: "John", lastName: "Michael Smith"
  // - "Mary Jane Watson" -> firstName: "Mary", lastName: "Jane Watson"
  // - "Jean-Luc Picard" -> firstName: "Jean-Luc", lastName: "Picard"
  // - "Dr. John Smith" -> firstName: "John", lastName: "Smith"
  
  // Check for common prefixes that should be ignored
  const prefixes = ['Dr.', 'Dr', 'Mr.', 'Mr', 'Ms.', 'Ms', 'Mrs.', 'Mrs', 'Miss', 'Prof.', 'Prof'];
  let startIndex = 0;
  
  // Skip prefixes
  if (prefixes.includes(nameParts[0])) {
    startIndex = 1;
  }
  
  // If we have 3+ parts after removing prefix, take first as first name, rest as last name
  if (nameParts.length - startIndex >= 3) {
    const firstName = nameParts[startIndex];
    const lastName = nameParts.slice(startIndex + 1).join(' ');
    return { firstName, lastName };
  }
  
  // If we have 2 parts after removing prefix
  if (nameParts.length - startIndex === 2) {
    return { 
      firstName: nameParts[startIndex], 
      lastName: nameParts[startIndex + 1] 
    };
  }
  
  // If we have 1 part after removing prefix
  if (nameParts.length - startIndex === 1) {
    return { firstName: nameParts[startIndex], lastName: '' };
  }
  
  // Fallback: first part as first name, rest as last name
  return { 
    firstName: nameParts[0], 
    lastName: nameParts.slice(1).join(' ') 
  };
}

/**
 * Tests the name splitting function with various name formats
 * This is for development/testing purposes
 */
export function testNameSplitter(): void {
  const testCases = [
    'John Smith',
    'Mary Jane Watson',
    'Jean-Luc Picard',
    'Dr. John Smith',
    'Mr. Robert Johnson',
    'Ms. Sarah Connor',
    'Prof. Albert Einstein',
    'Madonna',
    'Cher',
    'John Michael Smith Jr.',
    'Mary Elizabeth Watson-Smith',
    'O\'Connor',
    'Van Der Berg',
    'Dr. Mary Jane Watson-Smith',
    '  John   Smith  ', // Extra spaces
    '', // Empty string
    'John', // Single name
  ];
  
  console.log('Testing name splitter:');
  testCases.forEach(testCase => {
    const result = splitFullName(testCase);
    console.log(`"${testCase}" -> First: "${result.firstName}", Last: "${result.lastName}"`);
  });
}
